import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { card, input, label, btnPrimary, btnGhost, h1, badge, currency, fmtDate, lateness } from "./ui";
import { Plus, Pencil, Trash2, ChevronLeft, ChevronRight, Video, CalendarPlus, ExternalLink, Wallet, Check, X, UserX, NotebookPen, Search, ArrowUpDown, RotateCcw } from "lucide-react";

const empty = {
  patient_id: "", patient_name: "", date: "", time: "", duration_min: 50,
  status: "agendada", price: 0, payment_status: "pendente", payment_date: "", notes: "", meet_link: "", modality: "online", repeat_weeks: 0,
};
const STATUS = ["agendada", "realizada", "falta", "cancelada"];
const MONTHS = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
const WD = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const pad = (n) => String(n).padStart(2, "0");
const dstr = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;
const dotColor = (s) => (s === "realizada" ? "bg-[#8A9A82]" : s === "cancelada" ? "bg-[#C87A5E]" : "bg-[#5B6E5B]");

export default function Agenda() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [patients, setPatients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const openPatientNotes = (s) => {
    let pid = s.patient_id;
    if (!pid) {
      const p = patients.find((x) => x.name.toLowerCase() === (s.patient_name || "").toLowerCase());
      pid = p?.id;
    }
    if (!pid) return toast.error("Cadastre este paciente em Pacientes para abrir o prontuário.");
    navigate(`/dashboard/pacientes/${pid}?tab=ia`);
  };

  const openRoom = (s) => navigate(`/dashboard/sala/${s.id}`);

  const today = new Date();
  const [view, setView] = useState({ y: today.getFullYear(), m: today.getMonth() });
  const [selected, setSelected] = useState(dstr(today.getFullYear(), today.getMonth(), today.getDate()));

  const load = () => {
    api.get("/sessions").then(({ data }) => setItems(data)).catch(() => {});
    api.get("/patients").then(({ data }) => setPatients(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const byDate = items.reduce((acc, s) => { (acc[s.date] = acc[s.date] || []).push(s); return acc; }, {});

  const openNew = (date = "") => { setForm({ ...empty, date }); setEditId(null); setOpen(true); };
  const openEdit = (s) => { setForm({ ...empty, ...s, payment_date: s.payment_date || "", meet_link: s.meet_link || "" }); setEditId(s.id); setOpen(true); };

  // Jitsi preservado no código, oculto da interface — para reverter a alteração, mude para true.
  const JITSI_ENABLED = false;

  const genRoom = () => {
    const rnd = ((typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2) + Date.now().toString(36)).replace(/-/g, "");
    setForm((f) => ({ ...f, meet_link: `https://meet.jit.si/TacilainePsi-${rnd}` }));
    toast.success("Link Jitsi gerado (alternativa). Recomendado: Google Meet.");
  };

  const [payOpen, setPayOpen] = useState(false);
  const [payTarget, setPayTarget] = useState(null);
  const [fwdCount, setFwdCount] = useState(0);

  const setStatus = async (s, status) => {
    try {
      await api.patch(`/sessions/${s.id}/status`, { status });
      const labels = { realizada: "Marcado como atendido", falta: "Marcado como falta", cancelada: "Sessão cancelada", agendada: "Marcado como agendada" };
      toast.success(labels[status] || "Status atualizado");
      load();
    } catch { toast.error("Erro ao atualizar status."); }
  };

  const openPay = (s) => { setPayTarget(s); setFwdCount(0); setPayOpen(true); };

  const doPay = async (scope) => {
    try {
      const { data } = await api.post(`/sessions/${payTarget.id}/pay`, { scope, count: Number(fwdCount) || 0 });
      toast.success(data.paid > 1 ? `${data.paid} sessões marcadas como pagas.` : "Pagamento confirmado.");
      setPayOpen(false);
      load();
    } catch { toast.error("Erro ao confirmar pagamento."); }
  };

  const onPatient = (id) => {
    const p = patients.find((x) => x.id === id);
    setForm((f) => ({
      ...f, patient_id: id, patient_name: p ? p.name : f.patient_name,
      // valor individual do cadastro preenche o preço de NOVOS agendamentos (campo continua editável)
      ...(!editId && !f.price && p?.conditions?.session_price ? { price: p.conditions.session_price } : {}),
    }));
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.patient_name.trim() || !form.date || !form.time) return toast.error("Preencha paciente, data e horário.");
    const payload = { ...form, price: Number(form.price) || 0, duration_min: Number(form.duration_min) || 50, payment_date: form.payment_date || null, repeat_weeks: Number(form.repeat_weeks) || 0 };
    try {
      if (editId) await api.put(`/sessions/${editId}`, payload);
      else await api.post("/sessions", payload);
      toast.success("Sessão salva.");
      setOpen(false);
      load();
    } catch { toast.error("Erro ao salvar."); }
  };

  const del = async (id) => {
    if (!window.confirm("Excluir esta sessão?")) return;
    await api.delete(`/sessions/${id}`);
    toast.success("Sessão excluída.");
    load();
  };

  // calendar grid
  const startWd = new Date(view.y, view.m, 1).getDay();
  const daysInMonth = new Date(view.y, view.m + 1, 0).getDate();
  const cells = [...Array(startWd).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const move = (delta) => {
    let m = view.m + delta, y = view.y;
    if (m < 0) { m = 11; y--; } if (m > 11) { m = 0; y++; }
    setView({ y, m });
  };
  const todayStr = dstr(today.getFullYear(), today.getMonth(), today.getDate());
  const dayList = (byDate[selected] || []).slice().sort((a, b) => a.time.localeCompare(b.time));

  // ---- filtros da aba Lista (tudo client-side: o endpoint já retorna todas as sessões) ----
  const [quick, setQuick] = useState("mes"); // hoje | semana | mes | todas
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [patientQuery, setPatientQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("todos");
  const [payFilter, setPayFilter] = useState("todos");
  const [sortDir, setSortDir] = useState("asc");

  const norm = (t) => (t || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  // semana atual: segunda a domingo
  const mondayOff = (today.getDay() + 6) % 7;
  const monday = new Date(today); monday.setDate(today.getDate() - mondayOff);
  const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
  const weekFrom = dstr(monday.getFullYear(), monday.getMonth(), monday.getDate());
  const weekTo = dstr(sunday.getFullYear(), sunday.getMonth(), sunday.getDate());
  const monthFrom = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-01`;
  const monthTo = dstr(today.getFullYear(), today.getMonth(), new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate());
  const usingCustom = !!(dateFrom || dateTo); // datas personalizadas prevalecem sobre o chip

  const pickQuick = (k) => { setQuick(k); setDateFrom(""); setDateTo(""); };
  const clearFilters = () => { setQuick("mes"); setDateFrom(""); setDateTo(""); setPatientQuery(""); setStatusFilter("todos"); setPayFilter("todos"); setSortDir("asc"); };

  const filtered = items.filter((s) => {
    if (usingCustom) {
      if (dateFrom && s.date < dateFrom) return false;
      if (dateTo && s.date > dateTo) return false;
    } else if (quick === "hoje") { if (s.date !== todayStr) return false; }
    else if (quick === "semana") { if (s.date < weekFrom || s.date > weekTo) return false; }
    else if (quick === "mes") { if (s.date < monthFrom || s.date > monthTo) return false; }
    if (statusFilter !== "todos" && s.status !== statusFilter) return false;
    if (payFilter !== "todos" && s.payment_status !== payFilter) return false;
    if (patientQuery && !norm(s.patient_name).includes(norm(patientQuery))) return false;
    return true;
  }).sort((a, b) => {
    const k = (a.date + a.time).localeCompare(b.date + b.time);
    return sortDir === "asc" ? k : -k;
  });

  return (
    <div data-testid="agenda-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="font-body text-xs tracking-[0.35em] uppercase text-[#C87A5E]">Agenda</p>
          <h1 className={h1}>Sessões & pagamentos</h1>
        </div>
        <button className={btnPrimary} onClick={() => openNew(selected)} data-testid="new-session-btn"><Plus className="w-4 h-4" /> Nova sessão</button>
      </div>

      <Tabs defaultValue="calendario" className="mt-8">
        <TabsList className="bg-white border border-[#5B6E5B]/10">
          <TabsTrigger value="calendario" data-testid="tab-calendar">Calendário</TabsTrigger>
          <TabsTrigger value="lista" data-testid="tab-list">Lista</TabsTrigger>
          <TabsTrigger value="meet" data-testid="tab-meet-sync">Google Meet</TabsTrigger>
        </TabsList>

        {/* CALENDAR */}
        <TabsContent value="calendario" className="mt-6">
          <div className="grid lg:grid-cols-[1.6fr_1fr] gap-6">
            <div className={`${card} p-6`}>
              <div className="flex items-center justify-between mb-5">
                <button onClick={() => move(-1)} className={btnGhost} data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></button>
                <p className="font-display text-2xl text-[#2C332A]">{MONTHS[view.m]} {view.y}</p>
                <button onClick={() => move(1)} className={btnGhost} data-testid="cal-next"><ChevronRight className="w-4 h-4" /></button>
              </div>
              <div className="grid grid-cols-7 gap-1.5 mb-2">
                {WD.map((w) => <div key={w} className="text-center font-body text-xs uppercase tracking-wide text-[#7A8A7A] py-1">{w}</div>)}
              </div>
              <div className="grid grid-cols-7 gap-1.5">
                {cells.map((d, i) => {
                  if (!d) return <div key={`b${i}`} />;
                  const ds = dstr(view.y, view.m, d);
                  const dayItems = byDate[ds] || [];
                  const isSel = ds === selected;
                  const isToday = ds === todayStr;
                  return (
                    <button
                      key={ds}
                      onClick={() => setSelected(ds)}
                      data-testid={`cal-day-${d}`}
                      className={`relative aspect-square rounded-xl p-2 flex flex-col items-center justify-start transition-colors duration-200 border
                        ${isSel ? "bg-[#5B6E5B] text-[#FDFBF7] border-[#5B6E5B]" : "bg-[#FDFBF7] border-[#5B6E5B]/10 hover:bg-[#F5F0E6] text-[#2C332A]"}`}
                    >
                      <span className={`font-body text-sm ${isToday && !isSel ? "font-bold text-[#C87A5E]" : ""}`}>{d}</span>
                      {dayItems.length > 0 && (
                        <span className="flex gap-0.5 mt-1 flex-wrap justify-center">
                          {dayItems.slice(0, 3).map((s) => <span key={s.id} className={`w-1.5 h-1.5 rounded-full ${isSel ? "bg-[#FDFBF7]" : dotColor(s.status)}`} />)}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              <div className="flex gap-4 mt-5 font-body text-xs text-[#7A8A7A]">
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#5B6E5B]" /> agendada</span>
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#8A9A82]" /> realizada</span>
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#C87A5E]" /> cancelada</span>
              </div>
            </div>

            {/* selected day panel */}
            <div className={`${card} p-6`}>
              <div className="flex items-center justify-between mb-4">
                <p className="font-display text-xl text-[#2C332A]">{fmtDate(selected)}</p>
                <button onClick={() => openNew(selected)} className="inline-flex items-center gap-1.5 text-sm text-[#5B6E5B] hover:text-[#2C332A]" data-testid="day-add"><CalendarPlus className="w-4 h-4" /> Agendar</button>
              </div>
              {dayList.length === 0 && <p className="font-body text-sm text-[#7A8A7A]">Nenhuma sessão neste dia.</p>}
              <div className="space-y-3">
                {dayList.map((s) => (
                  <div key={s.id} className="rounded-xl bg-[#F5F0E6] p-4" data-testid="day-session">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-body font-medium text-[#2C332A]">{s.time} · {s.patient_name}</p>
                        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                          <span className={badge(s.status === "realizada")}>{s.status}</span>
                          <span className={badge(s.payment_status === "pago")}>{s.payment_status}</span>
                          {s.modality === "presencial" && <span className={badge(false)} data-testid="badge-presencial">presencial</span>}
                          {s.google_event_id && <span className={badge(true)} data-testid="badge-meet-vinculado">meet</span>}
                          {lateness(s) === "excedida" && <span className={badge(false)} data-testid="badge-tolerancia">tolerância excedida</span>}
                          <span className="font-body text-[11px] text-[#7A8A7A]" data-testid="day-session-ref">#{(s.id || "").replace(/-/g, "").slice(0, 6).toUpperCase()}</span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => openEdit(s)} className="text-[#5B6E5B] hover:text-[#2C332A]"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => del(s.id)} className="text-[#C87A5E] hover:text-[#B2644D]"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 mt-3">
                      <button onClick={() => setStatus(s, "realizada")} data-testid="status-realizada"
                        className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-body border transition-colors ${s.status === "realizada" ? "bg-[#5B6E5B] text-[#FDFBF7] border-[#5B6E5B]" : "border-[#5B6E5B]/25 text-[#4A5D4E] hover:bg-white"}`}>
                        <Check className="w-3.5 h-3.5" /> Atendido
                      </button>
                      <button onClick={() => setStatus(s, "falta")} data-testid="status-falta"
                        className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-body border transition-colors ${s.status === "falta" ? "bg-[#C87A5E] text-[#FDFBF7] border-[#C87A5E]" : "border-[#5B6E5B]/25 text-[#4A5D4E] hover:bg-white"}`}>
                        <UserX className="w-3.5 h-3.5" /> Falta
                      </button>
                      <button onClick={() => setStatus(s, "cancelada")} data-testid="status-cancelada"
                        className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-body border transition-colors ${s.status === "cancelada" ? "bg-[#8A6A5E] text-[#FDFBF7] border-[#8A6A5E]" : "border-[#5B6E5B]/25 text-[#4A5D4E] hover:bg-white"}`}>
                        <X className="w-3.5 h-3.5" /> Cancelou
                      </button>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 mt-3">
                      <button onClick={() => openRoom(s)} data-testid="session-open-room"
                        className="inline-flex items-center gap-2 rounded-full bg-[#5B6E5B] px-4 py-2 text-sm text-[#FDFBF7] hover:bg-[#4A5D4E] transition-colors">
                        <Video className="w-4 h-4" /> Sala de atendimento
                      </button>
                      <button onClick={() => openPatientNotes(s)} data-testid="session-patient-notes"
                        className="inline-flex items-center gap-2 rounded-full bg-[#C87A5E] px-4 py-2 text-sm text-[#FDFBF7] hover:bg-[#B2644D] transition-colors">
                        <NotebookPen className="w-4 h-4" /> Anotações da sessão
                      </button>
                      {s.payment_status !== "pago" ? (
                        <button onClick={() => openPay(s)} data-testid="confirm-payment"
                          className="inline-flex items-center gap-2 rounded-full bg-[#5B6E5B] px-4 py-2 text-sm text-[#FDFBF7] hover:bg-[#4A5D4E] transition-colors">
                          <Wallet className="w-4 h-4" /> Confirmar pagamento
                        </button>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-sm text-[#4A5D4E]"><Check className="w-4 h-4 text-[#5B6E5B]" /> Pago{s.payment_date ? ` em ${fmtDate(s.payment_date)}` : ""}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* LIST */}
        <TabsContent value="lista" className="mt-6">
          {/* barra de filtros */}
          <div className={`${card} p-4 mb-4`} data-testid="list-filters">
            <div className="flex flex-wrap items-center gap-2">
              {[["hoje", "Hoje"], ["semana", "Esta semana"], ["mes", "Este mês"], ["todas", "Todas as datas"]].map(([k, lbl]) => (
                <button key={k} onClick={() => pickQuick(k)} data-testid={`filter-chip-${k}`}
                  className={`rounded-full px-4 py-2 text-sm font-body border transition-colors ${!usingCustom && quick === k ? "bg-[#5B6E5B] text-[#FDFBF7] border-[#5B6E5B]" : "border-[#5B6E5B]/25 text-[#4A5D4E] hover:bg-[#F5F0E6]"}`}>
                  {lbl}
                </button>
              ))}
              {usingCustom && (
                <span className="rounded-full px-4 py-2 text-sm font-body bg-[#C87A5E]/15 text-[#B2644D] border border-[#C87A5E]/30" data-testid="filter-custom-note">Período personalizado</span>
              )}
              <button onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))} data-testid="sort-toggle"
                className="ml-auto inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-body border border-[#5B6E5B]/25 text-[#4A5D4E] hover:bg-[#F5F0E6] transition-colors">
                <ArrowUpDown className="w-3.5 h-3.5" /> {sortDir === "asc" ? "Data crescente" : "Data decrescente"}
              </button>
            </div>
            <div className="flex flex-wrap items-end gap-3 mt-3">
              <div>
                <label className={label}>Data inicial</label>
                <input type="date" className={input} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="filter-date-from" />
              </div>
              <div>
                <label className={label}>Data final</label>
                <input type="date" className={input} value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="filter-date-to" />
              </div>
              <div className="flex-1 min-w-[180px]">
                <label className={label}>Paciente</label>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#7A8A7A]" />
                  <input className={`${input} pl-9`} placeholder="Buscar por nome" value={patientQuery} onChange={(e) => setPatientQuery(e.target.value)} data-testid="filter-patient" />
                </div>
              </div>
              <div>
                <label className={label}>Status</label>
                <select className={input} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="filter-status">
                  <option value="todos">Todos</option>
                  {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className={label}>Pagamento</label>
                <select className={input} value={payFilter} onChange={(e) => setPayFilter(e.target.value)} data-testid="filter-payment">
                  <option value="todos">Todos</option>
                  <option value="pendente">pendente</option>
                  <option value="pago">pago</option>
                </select>
              </div>
              <button onClick={clearFilters} className={`${btnGhost} inline-flex items-center gap-1.5`} data-testid="filter-clear">
                <RotateCcw className="w-3.5 h-3.5" /> Limpar filtros
              </button>
            </div>
            <p className="font-body text-xs text-[#7A8A7A] mt-3" data-testid="filter-count">
              {filtered.length} {filtered.length === 1 ? "sessão encontrada" : "sessões encontradas"}
            </p>
          </div>

          <div className={`${card} overflow-hidden`}>
            <div className="hidden md:grid grid-cols-[1.3fr_1fr_.8fr_.9fr_.9fr_.9fr] gap-4 px-6 py-4 bg-[#F5F0E6] font-body text-xs uppercase tracking-wider text-[#7A8A7A]">
              <span>Paciente</span><span>Data / Hora</span><span>Status</span><span>Valor</span><span>Pagamento</span><span></span>
            </div>
            {items.length === 0 && <p className="p-6 font-body text-sm text-[#7A8A7A]">Nenhuma sessão registrada.</p>}
            {items.length > 0 && filtered.length === 0 && <p className="p-6 font-body text-sm text-[#7A8A7A]" data-testid="filter-empty">Nenhuma sessão encontrada com esses filtros.</p>}
            <div className="divide-y divide-[#5B6E5B]/10">
              {filtered.map((s) => (
                <div key={s.id} className="grid grid-cols-2 md:grid-cols-[1.3fr_1fr_.8fr_.9fr_.9fr_.9fr] gap-2 md:gap-4 px-6 py-4 items-center" data-testid="session-row">
                  <span className="font-body text-[#2C332A] font-medium">{s.patient_name}</span>
                  <span className="font-body text-sm text-[#4A5D4E]">{fmtDate(s.date)} · {s.time}</span>
                  <span className="flex items-center gap-1.5"><span className={badge(s.status === "realizada")}>{s.status}</span>{s.modality === "presencial" && <span className={badge(false)}>presencial</span>}{s.google_event_id && <span className={badge(true)}>meet</span>}</span>
                  <span className="font-body text-sm text-[#4A5D4E]">{currency(s.price)}</span>
                  <span><span className={badge(s.payment_status === "pago")}>{s.payment_status}</span></span>
                  <span className="flex justify-end gap-2 items-center">
                    <button onClick={() => openRoom(s)} className="text-[#5B6E5B] hover:text-[#2C332A]" data-testid="row-open-room" title="Sala de atendimento"><Video className="w-4 h-4" /></button>
                    <button onClick={() => openPatientNotes(s)} className="text-[#C87A5E] hover:text-[#B2644D]" data-testid="row-patient-notes" title="Anotações da sessão (IA + prontuário)"><NotebookPen className="w-4 h-4" /></button>
                    <button onClick={() => openEdit(s)} className="text-[#5B6E5B] hover:text-[#2C332A]" data-testid="edit-session"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => del(s.id)} className="text-[#C87A5E] hover:text-[#B2644D]" data-testid="delete-session"><Trash2 className="w-4 h-4" /></button>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* GOOGLE MEET — sincronização mensal */}
        <TabsContent value="meet" className="mt-6">
          <MeetSyncPanel />
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FDFBF7] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl text-[#2C332A]">{editId ? "Editar sessão" : "Nova sessão"}</DialogTitle>
            <DialogDescription className="sr-only">Formulário de sessão</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4" data-testid="session-form">
            <div>
              <label className={label}>Paciente</label>
              <select className={input} value={form.patient_id} onChange={(e) => onPatient(e.target.value)} data-testid="session-patient">
                <option value="">— selecionar / digitar abaixo —</option>
                {patients.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <input className={`${input} mt-2`} placeholder="Nome do paciente" value={form.patient_name} onChange={(e) => setForm({ ...form, patient_name: e.target.value })} data-testid="session-patient-name" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className={label}>Data</label><input type="date" className={input} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="session-date" /></div>
              <div><label className={label}>Horário</label><input type="time" className={input} value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="session-time" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className={label}>Modalidade</label>
                <select className={input} value={form.modality || "online"} onChange={(e) => setForm({ ...form, modality: e.target.value })} data-testid="session-modality">
                  <option value="online">Online (Google Meet)</option>
                  <option value="presencial">Presencial</option>
                </select>
              </div>
              <div><label className={label}>Status</label>
                <select className={input} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="session-status">
                  {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            {(form.modality || "online") === "presencial" ? (
              <p className="font-body text-xs text-[#7A8A7A]" data-testid="presencial-note">Sessões presenciais não geram evento no Google Agenda nem link do Meet.</p>
            ) : (
              <p className="font-body text-xs text-[#7A8A7A]">Sessões online do mês corrente ganham o link do Meet na hora; meses futuros entram na sincronização do dia 1º (aba Google Meet).</p>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div><label className={label}>Valor (R$)</label><input type="number" step="0.01" className={input} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="session-price" /></div>
              <div><label className={label}>Pagamento</label>
                <select className={input} value={form.payment_status} onChange={(e) => setForm({ ...form, payment_status: e.target.value })} data-testid="session-payment">
                  <option value="pendente">pendente</option>
                  <option value="pago">pago</option>
                </select>
              </div>
            </div>
            <div>
              <label className={label}>Data do pagamento</label><input type="date" className={input} value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} />
            </div>

            <div>
              <label className={label}>Videochamada</label>
              <input className={input} placeholder="Cole aqui o link do Google Meet" value={form.meet_link} onChange={(e) => setForm({ ...form, meet_link: e.target.value })} data-testid="session-meet" />
              <div className="flex flex-wrap gap-2 mt-2">
                {JITSI_ENABLED && (
                <button type="button" className={btnPrimary} onClick={genRoom} data-testid="auto-meet">
                  <Video className="w-4 h-4" /> Gerar link automático
                </button>
                )}
                <button type="button" className={btnGhost} onClick={() => window.open("https://meet.google.com/new", "_blank")} data-testid="create-meet" title="Abrir Google Meet e copiar o link">
                  <ExternalLink className="w-4 h-4" /> Usar Google Meet
                </button>
              </div>
              <p className="font-body text-xs text-[#7A8A7A] mt-1.5">Crie a reunião no Google Meet, copie o link e cole aqui — ele fica salvo na sessão e é enviado ao paciente nas confirmações e lembretes.</p>
              {form.meet_link && (
                <a href={form.meet_link} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-sm text-[#5B6E5B] hover:text-[#2C332A]"><ExternalLink className="w-3.5 h-3.5" /> Testar link</a>
              )}
            </div>

            <div><label className={label}>Observações</label><textarea rows={2} className={`${input} resize-none`} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            {!editId && (
              <div className="rounded-xl bg-[#F5F0E6] p-4">
                <label className={label}>Repetir semanalmente</label>
                <div className="flex items-center gap-2">
                  <span className="font-body text-sm text-[#4A5D4E]">Por</span>
                  <input type="number" min="0" max="52" className={`${input} w-24`} value={form.repeat_weeks} onChange={(e) => setForm({ ...form, repeat_weeks: e.target.value })} data-testid="session-repeat" />
                  <span className="font-body text-sm text-[#4A5D4E]">semanas (no mesmo dia e horário)</span>
                </div>
                <p className="font-body text-xs text-[#7A8A7A] mt-1.5">Deixe 0 ou 1 para uma única sessão. Ex.: 8 cria 8 sessões semanais.</p>
              </div>
            )}
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" className={btnGhost} onClick={() => setOpen(false)}>Cancelar</button>
              <button type="submit" className={btnPrimary} data-testid="save-session">Salvar</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="bg-[#FDFBF7]">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#2C332A]">Confirmar pagamento</DialogTitle>
            <DialogDescription className="font-body text-sm text-[#7A8A7A]">
              {payTarget ? `${payTarget.patient_name} · ${fmtDate(payTarget.date)} · ${payTarget.time}` : ""}
            </DialogDescription>
          </DialogHeader>
          {payTarget && (() => {
            const ps = items.filter((s) => payTarget.patient_id ? s.patient_id === payTarget.patient_id : s.patient_name === payTarget.patient_name);
            const paid = ps.filter((s) => s.payment_status === "pago").reduce((a, s) => a + Number(s.price || 0), 0);
            const pend = ps.filter((s) => s.payment_status !== "pago" && s.status !== "cancelada").reduce((a, s) => a + Number(s.price || 0), 0);
            const pendCount = ps.filter((s) => s.payment_status !== "pago" && s.status !== "cancelada").length;
            return (
              <div className="flex gap-3 mb-1" data-testid="pay-summary">
                <div className="flex-1 rounded-xl bg-[#5B6E5B]/8 p-3 text-center">
                  <p className="font-body text-[11px] uppercase tracking-wider text-[#7A8A7A]">Já pago</p>
                  <p className="font-display text-lg text-[#4A5D4E]" data-testid="pay-summary-paid">{currency(paid)}</p>
                </div>
                <div className="flex-1 rounded-xl bg-[#C87A5E]/12 p-3 text-center">
                  <p className="font-body text-[11px] uppercase tracking-wider text-[#B2644D]">Pendente ({pendCount})</p>
                  <p className="font-display text-lg text-[#B2644D]" data-testid="pay-summary-pending">{currency(pend)}</p>
                </div>
              </div>
            );
          })()}
          <div className="space-y-3" data-testid="pay-dialog">
            <button onClick={() => doPay("single")} data-testid="pay-single"
              className="w-full text-left rounded-xl border border-[#5B6E5B]/20 p-4 hover:bg-white transition-colors">
              <p className="font-body font-medium text-[#2C332A]">Somente esta sessão</p>
              <p className="font-body text-xs text-[#7A8A7A] mt-0.5">Marca apenas esta sessão como paga.</p>
            </button>

            <div className="rounded-xl border border-[#5B6E5B]/20 p-4">
              <p className="font-body font-medium text-[#2C332A]">Adiantado — esta + próximas pendentes</p>
              <p className="font-body text-xs text-[#7A8A7A] mt-0.5 mb-2">Deixe 0 para quitar todas as próximas pendentes, ou escolha quantas.</p>
              <div className="flex items-center gap-2">
                <span className="font-body text-sm text-[#4A5D4E]">Próximas:</span>
                <input type="number" min="0" max="52" className={`${input} w-24`} value={fwdCount}
                  onChange={(e) => setFwdCount(e.target.value)} data-testid="pay-forward-count" />
                <button onClick={() => doPay("forward")} data-testid="pay-forward" className={`${btnPrimary} ml-auto`}>Quitar</button>
              </div>
            </div>

            <button onClick={() => doPay("backward")} data-testid="pay-backward"
              className="w-full text-left rounded-xl border border-[#5B6E5B]/20 p-4 hover:bg-white transition-colors">
              <p className="font-body font-medium text-[#2C332A]">Atrasado — esta + anteriores pendentes</p>
              <p className="font-body text-xs text-[#7A8A7A] mt-0.5">Quita esta e todas as sessões anteriores pendentes deste paciente.</p>
            </button>
          </div>
          <div className="flex justify-end pt-2">
            <button type="button" className={btnGhost} onClick={() => setPayOpen(false)} data-testid="pay-cancel">Cancelar</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const TRIGGER_LABEL = { manual: "Manual (pelo painel)", "auto-mensal": "Automática (dia 1º)", "auto-retentativa": "Verificação diária" };
const fmtDT = (iso) => {
  try { return new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

function MeetSyncPanel() {
  const now = new Date();
  const defMonth = `${now.getFullYear()}-${pad(now.getMonth() + 1)}`;
  const [month, setMonth] = useState(defMonth);
  const [st, setSt] = useState(null);
  const [running, setRunning] = useState(false);

  const load = async (m) => {
    try { const { data } = await api.get(`/google/meet-sync?month=${m || month}`); setSt(data); }
    catch { toast.error("Não consegui carregar os dados da sincronização."); }
  };
  useEffect(() => { load(defMonth); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onMonth = (m) => { setMonth(m); if (m) load(m); };

  const sync = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/google/meet-sync", { month });
      if (data.errors?.length) toast.warning(`Sincronização concluída com ${data.errors.length} erro(s). Veja o detalhe abaixo.`);
      else toast.success(`Mês sincronizado: ${data.created} evento(s) criado(s), ${data.updated} atualizado(s).`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao sincronizar. Tente novamente.");
      await load();
    } finally { setRunning(false); }
  };

  const stats = [
    { label: "Atendimentos online no mês", value: st?.agendados_online, tid: "meet-sync-agendados" },
    { label: "Eventos já vinculados", value: st?.vinculados, tid: "meet-sync-vinculados" },
    { label: "Pendentes de sincronização", value: st?.pendentes, tid: "meet-sync-pendentes" },
    { label: "Falhas na última sincronização", value: st?.falhas, tid: "meet-sync-falhas", warn: true },
  ];
  const last = st?.last;

  return (
    <div className={`${card} p-6`} data-testid="meet-sync-panel">
      <p className="inline-flex items-center gap-2 font-display text-xl text-[#2C332A]"><Video className="w-5 h-5 text-[#5B6E5B]" /> Sincronização mensal do Google Meet</p>
      <p className="font-body text-sm text-[#7A8A7A] mt-3">
        Os eventos do Google Agenda e os links do Meet são criados mês a mês, somente para atendimentos <strong>online</strong>.
        No dia 1º de cada mês a sincronização roda sozinha às 06:00 (com verificação diária de segurança), e todo novo
        agendamento online do mês corrente ganha o link na hora. Você também pode sincronizar qualquer mês por aqui.
        Links criados manualmente nunca são substituídos. A conexão da conta Google é feita em Segurança.
      </p>

      <div className="flex flex-wrap items-end gap-3 mt-5">
        <div>
          <label className={label}>Mês da sincronização</label>
          <input type="month" className={input} value={month} onChange={(e) => onMonth(e.target.value)} data-testid="meet-sync-month" />
        </div>
        <button onClick={sync} disabled={running || !month} className={`${btnPrimary} inline-flex items-center gap-2`} data-testid="meet-sync-button">
          <Video className="w-4 h-4" /> {running ? "Sincronizando..." : "Sincronizar mês"}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
        {stats.map((k) => (
          <div key={k.tid} className={`rounded-xl p-3 text-center ${k.warn && k.value ? "bg-[#C87A5E]/12" : "bg-[#5B6E5B]/8"}`}>
            <p className="font-body text-[11px] uppercase tracking-wider text-[#7A8A7A]">{k.label}</p>
            <p className={`font-display text-lg ${k.warn && k.value ? "text-[#B2644D]" : "text-[#4A5D4E]"}`} data-testid={k.tid}>{k.value ?? "—"}</p>
          </div>
        ))}
      </div>
      {st?.link_manual > 0 && (
        <p className="font-body text-xs text-[#7A8A7A] mt-2" data-testid="meet-sync-manuais">
          {st.link_manual} sessão(ões) com link criado manualmente — preservadas, não entram na sincronização.
        </p>
      )}

      {last && (
        <div className="mt-5 rounded-xl border border-[#5B6E5B]/15 p-4" data-testid="meet-sync-last">
          <p className="font-body text-sm font-medium text-[#2C332A]">
            Última sincronização: {fmtDT(last.ran_at)} · {TRIGGER_LABEL[last.trigger] || last.trigger} · mês {last.month}
          </p>
          <p className="font-body text-sm text-[#4A5D4E] mt-1.5">
            {last.created} criado(s) · {last.updated} atualizado(s) · {last.skipped} ignorado(s)
            {last.removed ? ` · ${last.removed} removido(s)` : ""} · {last.errors?.length || 0} erro(s)
          </p>
          {last.errors?.length > 0 && (
            <ul className="mt-2 space-y-1 font-body text-xs text-[#B2644D]" data-testid="meet-sync-errors">
              {last.errors.map((er, i) => (
                <li key={i}>
                  {er.data ? `${fmtDate(er.data)} ` : ""}{er.ref ? <strong>#{er.ref}</strong> : null}{er.ref ? " — " : ""}{er.motivo}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
